import { yupResolver } from '@hookform/resolvers/yup';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Button } from '../../../../../../web/components-v2/Button';
import { StarRating } from '../../../../../../web/components/surveys/StarRating';
import { IFormField, ISatisfactionSurvey, ISatisfactionSurveyResponse } from '../../../../../models/Api';
import { useErrorMessagesContext } from '../../../../../components/ErrorModalProvider/context';
import { buildContactInfoSchemaFields } from '../../../shared/buildContactInfoSchemaFields';
import { ContactInfoStep } from '../../../Custom/components/CustomSurvey/CustomSurveyResponseForm/ContactInfoStep';
import type { IFormValues } from '../../../Custom/components/CustomSurvey/CustomSurveyResponseForm/types';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	onRequestSubmit?(response: ISatisfactionSurveyResponse): Promise<any>;
	styles?: StyleDeclarationValue[];
	survey: ISatisfactionSurvey;
}

const contactInfoDefaultValues: IFormValues = {
	email: '',
	firstName: '',
	lastName: '',
	phoneNumber: '',
	birthday: '',
	address1: '',
	address2: '',
	city: '',
	stateProvince: '',
	postalCode: '',
	country: '',
	customProperties: {},
};

const collectsContactInfo = (survey: ISatisfactionSurvey) => {
	const options = survey.options;
	return Boolean(
		options?.contactNameOptions?.collect ||
			options?.contactEmailOptions?.collect ||
			options?.contactPhoneNumberOptions?.collect ||
			options?.contactBirthdayOptions?.collect ||
			options?.contactAddressOptions?.collect
	);
};

const toContactInfo = (values: IFormValues): ISatisfactionSurveyResponse['contactInfo'] => {
	const contactInfo = {
		email: values.email?.trim(),
		firstName: values.firstName?.trim(),
		lastName: values.lastName?.trim(),
		phoneNumber: values.phoneNumber?.trim(),
		birthday: values.birthday?.trim(),
		address: {
			address1: values.address1?.trim(),
			address2: values.address2?.trim(),
			city: values.city?.trim(),
			stateProvince: values.stateProvince?.trim(),
			postalCode: values.postalCode?.trim(),
			country: values.country?.trim(),
		},
	};
	const hasAddress = Object.values(contactInfo.address).some(Boolean);
	const hasValue =
		Boolean(
			contactInfo.email ||
				contactInfo.firstName ||
				contactInfo.lastName ||
				contactInfo.phoneNumber ||
				contactInfo.birthday
		) || hasAddress;
	if (!hasValue) {
		return undefined;
	}
	if (!hasAddress) {
		const { address: _address, ...rest } = contactInfo;
		return rest;
	}
	return contactInfo;
};

export const SatisfactionSurvey: React.FC<IProps> = props => {
	const { className, styles = [], survey, onRequestSubmit } = props;
	const ratingStarStyles = React.useRef<StyleDeclarationValue[]>([styleSheet.ratingStar]).current;
	const { pushError } = useErrorMessagesContext();

	const [rating, setRating] = React.useState<number>(0);

	// A personalized link already identifies the respondent, so only anonymous (e.g. QR code) visitors see the fields.
	const showContactInfo = collectsContactInfo(survey) && survey.hasLinkedContact !== true;

	// Hidden fields must not validate: a personalized link hides the contact step, and leaving required
	// validators active would silently block the submit with errors the visitor can never see.
	const validationSchema = React.useMemo(
		() => yup.object().shape(showContactInfo ? buildContactInfoSchemaFields(survey.options) : {}),
		[survey, showContactInfo]
	);
	const { handleSubmit, control, formState } = useForm<IFormValues>({
		defaultValues: contactInfoDefaultValues,
		resolver: yupResolver(validationSchema),
	});

	// Check if this is the only question (no custom form fields or all fields are hidden)
	const hasAdditionalQuestions =
		survey.customForm?.fields?.some((field: IFormField<string>) => !field.isHidden) || false;
	const introStyle = hasAdditionalQuestions ? styleSheet.intro : styleSheet.introLargeText;

	const onSubmit = (values: IFormValues) => {
		const response: ISatisfactionSurveyResponse = {
			rating,
		};
		const contactInfo = showContactInfo ? toContactInfo(values) : undefined;
		if (contactInfo) {
			response.contactInfo = contactInfo;
		}
		onRequestSubmit?.(response);
	};

	const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		if (rating === 0) {
			event.preventDefault();
			pushError(['Please provide a rating to continue.']);
			return;
		}
		handleSubmit(onSubmit)(event);
	};

	return (
		<form
			className={`${css(styleSheet.container, ...styles)} satisfaction-survey ${className || ''}`}
			onSubmit={onFormSubmit}
		>
			{showContactInfo ? (
				<div className={css(styleSheet.contactInfoContainer)}>
					<ContactInfoStep survey={survey} control={control} formState={formState} showErrors={formState.isSubmitted} />
				</div>
			) : null}
			<div className={css(introStyle)}>{survey.intro}</div>
			<figure className={css(styleSheet.ratingContainer)}>
				<StarRating
					animateRatingChanges={true}
					fillColor='#F3CE0D'
					onValueChanged={setRating}
					starStyles={ratingStarStyles}
					total={5}
					value={rating}
					showEmoji={false}
				/>
			</figure>
			<Button type='submit' variant='primary' styles={[styleSheet.submitButton]}>
				Submit Star Rating
			</Button>
		</form>
	);
};
